<template>
  <div id="internal-load-table">
    <v-data-table
      :headers="tableHeaders"
      :loading="loading"
      :items="sortedData"
      hide-default-header
      hide-default-footer
      disable-pagination
      :sort-by="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template v-slot:header="{ props }">
        <thead class="v-data-table-header">
          <tr>
            <th
              v-for="head in props.headers"
              :key="head.value"
              class="sortable"
              @click="sortTable(head)"
            >
              <div :style="{width: head.width}">
                <v-tooltip
                  v-if="head.tooltip"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <span
                      v-on="on"
                    >{{ head.text }}</span>
                  </template>
                  <span>{{ head.tooltip }}</span>
                </v-tooltip>
                <div v-else>
                  {{ head.text }}
                </div>
              </div>
            </th>
          </tr>
        </thead>
      </template>
      <!--<editor-fold desc="ITEM SLOT">-->
      <template v-slot:item="{ item }">
        <internal-load-table-row
          v-if="!loading"
          :training-load="item"
          :max-values="maxValues"
          :show-menstrual-period="showMenstrualPeriod"
          :show-wellness-data="showWellnessData"
          @click="onTableRowClick"
        />
      </template>
      <!--</editor-fold>-->
      <!--<editor-fold desc="PROGRESS SLOT">-->
      <template slot="progress">
        <!-- override default progress bar -->
        <div
          class="pa-5 d-flex justify-center align-center"
          :style="'min-height: ' + (data.length * 49)+'px'"
        >
          <v-progress-circular
            indeterminate
            size="48"
            width="4"
            color="grey lighten-2"
          />
        </div>
      </template>
      <!--</editor-fold>-->
    </v-data-table>
  </div>
</template>

<script>
import InternalLoadTableRow from '@/components/training-load/InternalLoadTableRow'

export default {
  name: 'InternalLoadTable',
  components: { InternalLoadTableRow },
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    maxValues: {
      type: Object,
      required: true
    },
    pagination: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    sortBy: 'last_name',
    sortDesc: false
  }),
  computed: {
    tableHeaders() {
      const headers = [
        { key: 'name', align: 'start', text: this.$t('table_headers.name'), value: 'last_name' },
        ...(this.$can('view-event-feedback') ? [{ key: 'tl', text: this.$t('individual_analysis.training_load') }] : []),
        { key: 'dailyLoad', width: '75px', text: this.$t('table_headers.main_load.daily_tl'), value: 'latest_stats.DL' },
        { key: 'acuteLoad', width: '75px', text: this.$t('table_headers.main_load.acute'), value: 'latest_stats.AL' },
        { key: 'chronicLoad', width: '75px', text: this.$t('table_headers.main_load.chronic'), value: 'latest_stats.CL' },
        { key: 'acwr', text: this.$t('table_headers.main_load.acwr'), value: 'latest_stats.ACWR' },
        { key: 'freshness', text: this.$t('table_headers.main_load.freshness'), value: 'latest_stats.FRES' },
        ...(this.showWellnessData ? [{ key: 'fatigue', text: this.$t('table_headers.player_overview.fatigue'), value: 'latest_stats.FAT' }] : []),
        ...(this.showWellnessData ? [{ value: 'latest_stats.GMS', text: this.$t('table_headers.main_load.muscle_soreness'), tooltip: this.$t('table_headers.main_load.muscle_soreness.tooltip') }] : []),
        ...(this.showWellnessData ? [{ value: 'latest_stats.STL', text: this.$t('table_headers.main_load.stress') }] : []),
        ...(this.showWellnessData ? [{ value: 'latest_stats.MOO', text: this.$t('table_headers.main_load.mood') }] : []),
        ...(this.showWellnessData ? [{ key: 'sleepStatus', text: this.$t('table_headers.main_load.sleep'), value: 'latest_stats.SLQ' }] : []),
        ...(this.showWellnessData ? [{ key: 'sleepDuration', width: '40px', text: this.$t('table_headers.main_load.sleep_duration'), tooltip: this.$t('table_headers.main_load.sleep_duration.tooltip'), value: 'latest_stats.SLD' }] : []),
        { value: 'latest_stats.highest_pain', text: this.$t('table_headers.main_load.pain') },
        { value: 'latest_stats.sick', text: this.$t('table_headers.main_load.sickness') }
      ]

      if (this.showMenstrualPeriod) {
        headers.push({ value: 'latest_stats.FMENSACT', text: this.$t('table_headers.individual_load.menstrual_period') })
        headers.push({ value: 'latest_stats.cycle_day', text: this.$t('table_headers.individual_load.menstrual_cycle_phase') })
      }

      return headers
    },
    showMenstrualPeriod() {
      return this.data.some(p => p.gender === 'female')
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    sortedData() {
      const sorted = [...this.data]

      return sorted.sort((a, b) => {
        let aValue = null
        let bValue = null

        if (this.sortBy && this.sortBy.includes('latest_stats')) {
          aValue = a.latest_stats ? a.latest_stats[this.sortBy.split('.')[1]] : null
          bValue = b.latest_stats ? b.latest_stats[this.sortBy.split('.')[1]] : null
        } else {
          aValue = a[this.sortBy] || ''
          bValue = b[this.sortBy] || ''
        }

        if (this.sortDesc) {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        } else {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        }
      })
    }
  },
  methods: {
    onTableRowClick(playerId) {
      this.$emit('tableRowClick', playerId)
    },
    sortTable(header) {
      if (this.sortBy === header.value) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = header.value
        this.sortDesc = false
      }
    }
  }
}
</script>
